import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, X as CloseIcon } from "lucide-react";
import { VentLogo } from "@/components/VentLogo";
import { Button } from "@/components/ui/button";
import { openExternalUrl } from "@/hooks/useExternalLink";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { APP_STORE_URL, PLAY_STORE_URL, shouldShowAppStore, shouldShowPlayStore } from "@/lib/storeLinks";
import { cn } from "@/lib/utils";

const INSTAGRAM_URL = "https://www.instagram.com/the.vent.app";

const LandingHeader = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const isNative = useNativePlatform();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const navItems: { label: string; to?: string; href?: string }[] = [
    { label: "Home", to: "/" },
    { label: "About", to: "/about" },
    { label: "Store", href: "https://theventapp.myshopify.com" },
    { label: "Partnerships", to: "/partnerships" },
    { label: "Contact Us", to: "/contact" },
  ];

  return (
    <>
      <header
        className={cn(
          "fixed top-0 inset-x-0 z-50 transition-colors duration-300",
          scrolled
            ? "bg-background/85 backdrop-blur-md border-b border-border"
            : "bg-transparent border-b border-transparent"
        )}
      >
        <div className="mx-auto max-w-7xl px-5 md:px-10 min-h-16 md:min-h-20 py-2 flex items-center justify-between">
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            aria-expanded={open}
            className="group flex flex-col justify-center gap-[5px] w-10 h-10 -ml-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="block h-px w-6 bg-foreground transition-transform duration-300 group-hover:translate-x-0.5" />
            <span className="block h-px w-6 bg-foreground" />
            <span className="block h-px w-4 bg-foreground transition-all duration-300 group-hover:w-6" />
          </button>

          <Link to="/" aria-label="Vent home" className="absolute left-1/2 -translate-x-1/2">
            <VentLogo showAnimation={false} />
          </Link>

          <Link to="/auth">
            <Button size="sm" className="rounded-full px-5 tracking-wide transition-transform duration-300 hover:-translate-y-0.5">
              Get Started
            </Button>
          </Link>
        </div>
      </header>

      {/* Full-screen navigation overlay */}
      <div
        className={cn(
          "fixed inset-0 z-[70] transition-opacity duration-300",
          open ? "opacity-100" : "pointer-events-none opacity-0"
        )}
        aria-hidden={!open}
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          tabIndex={-1}
          className={cn(
            "absolute inset-0 bg-ink text-ink-foreground outline-none transition-transform duration-300 ease-out",
            open ? "translate-y-0" : "-translate-y-3"
          )}
        >
          <div className="mx-auto max-w-7xl px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
            <button
              type="button"
              onClick={close}
              aria-label="Close menu"
              className="w-10 h-10 -ml-2 flex items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-foreground"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
            <span className="font-display text-sm tracking-[0.35em] uppercase text-ink-foreground/70">
              Menu
            </span>
          </div>

          <nav className="mx-auto max-w-7xl px-5 md:px-10 mt-10 md:mt-20">
            <ul className="space-y-3 md:space-y-5">
              {navItems.map((item) => (
                <li key={item.label}>
                  {item.to ? (
                    <Link
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className="font-display uppercase text-4xl md:text-7xl leading-tight tracking-tight text-ink-foreground/90 hover:text-ink-foreground transition-colors duration-300 inline-block"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        openExternalUrl(item.href!);
                      }}
                      className="font-display uppercase text-4xl md:text-7xl leading-tight tracking-tight text-ink-foreground/90 hover:text-ink-foreground transition-colors duration-300"
                    >
                      {item.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>

            <div className="mt-12 md:mt-20 flex flex-wrap items-center gap-6 border-t border-ink-foreground/15 pt-8">
              <button
                onClick={() => openExternalUrl(INSTAGRAM_URL)}
                aria-label="Vent on Instagram"
                className="text-ink-foreground/70 hover:text-ink-foreground transition-colors"
              >
                <Instagram className="h-5 w-5" />
              </button>
              {!isNative && shouldShowAppStore() && (
                <button
                  onClick={() => openExternalUrl(APP_STORE_URL)}
                  className="text-sm text-ink-foreground/70 hover:text-ink-foreground transition-colors"
                >
                  Download on the App Store
                </button>
              )}
              {!isNative && shouldShowPlayStore() && (
                <button
                  onClick={() => openExternalUrl(PLAY_STORE_URL)}
                  className="text-sm text-ink-foreground/70 hover:text-ink-foreground transition-colors"
                >
                  Get it on Google Play
                </button>
              )}
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};

export default LandingHeader;
